import app from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import 'firebase/compat/firestore';
import 'firebase/compat/analytics';

import i18n from '../../i18n';
import authEmailApi from '../../services/authEmailApi';
import orgApi from '../../services/orgApi';
import { ADMIN as ROLE_ADMIN, USER as ROLE_USER, GUEST as ROLE_GUEST } from '../../constants/roles';

// =============================================================================
// LOGGABLE EVENTS CONFIG
// Set any event to `false` to disable logging for that specific action.
// This is the single place to control what gets recorded in the org activity log.
// =============================================================================
export const LOGGABLE_EVENTS = {
  // --- Auth ---
  USER_SIGNED_IN: true,
  USER_SIGNED_IN_GOOGLE: true,
  USER_SIGNED_IN_MICROSOFT: true,
  USER_SIGNED_OUT: true,
  USER_PASSWORD_RESET: false,
  USER_PASSWORD_UPDATED: false,
  USER_EMAIL_VERIFICATION_SENT: false,

  // --- Projects ---
  PROJECT_CREATED: true,
  PROJECT_RENAMED: true,
  PROJECT_UPDATED: true,
  PROJECT_DELETED: true,

  // --- Drawings / Pages ---
  PAGE_ANNOTATED: true,
  PAGE_SAVED: true,

  // --- Planning / Gantt ---
  PLANNING_SAVED: true,

  // --- Assemblies ---
  ASSEMBLY_CREATED: true,
  ASSEMBLY_DELETED: true,
  ASSEMBLY_UPDATED: true,
  ASSEMBLY_RENAMED: true,
  ORG_ASSEMBLY_CREATED: true,
  ORG_ASSEMBLY_DELETED: true,

  // --- Resource Management ---
  RESOURCE_PROJECT_SAVED: true,
  RESOURCE_PROJECT_UPDATED: true,
  RESOURCE_PROJECT_DELETED: true,
  RESOURCE_SAVED: true,
  RESOURCE_UPDATED: true,
  RESOURCE_DELETED: true,
  RESOURCE_ASSIGNMENT_SAVED: true,
  RESOURCE_ASSIGNMENT_DELETED: true,
  RESOURCE_STATE_SAVED: false,

  // --- Messages ---
  MESSAGE_SENT: true,
  MESSAGE_EDITED: true,
  MESSAGE_DELETED: true,

  // --- Documents (VControl) ---
  DOCUMENT_UPLOADED: true,
  DOCUMENT_DELETED: true,

  // --- Inventory: Warehouses ---
  WAREHOUSE_CREATED: true,
  WAREHOUSE_UPDATED: true,
  WAREHOUSE_DELETED: true,
  STORAGE_LOCATION_CREATED: true,
  STORAGE_LOCATION_UPDATED: true,

  // --- Inventory: Suppliers ---
  SUPPLIER_CREATED: true,
  SUPPLIER_UPDATED: true,
  SUPPLIER_DELETED: true,

  // --- Inventory: Products ---
  PRODUCT_CREATED: true,
  PRODUCT_UPDATED: true,
  PRODUCT_DELETED: true,

  // --- Inventory: Operations ---
  PURCHASE_ORDER_CREATED: true,
  PURCHASE_ORDER_UPDATED: true,
  PURCHASE_ORDER_DELETED: true,
  SALES_ORDER_CREATED: true,
  SALES_ORDER_UPDATED: true,
  SALES_ORDER_DELETED: true,
  SHIPMENT_CREATED: true,
  SHIPMENT_UPDATED: true,
  SHIPMENT_DELETED: true,
  RECEIPT_CREATED: true,
  RECEIPT_UPDATED: true,
  RECEIPT_DELETED: true,
  STOCK_TRANSFER_CREATED: true,
  STOCK_TRANSFER_UPDATED: true,
  STOCK_TRANSFER_DELETED: true,
  STOCK_RESERVATION_CREATED: true,
  STOCK_RESERVATION_UPDATED: true,
  STOCK_RESERVATION_DELETED: true,
  RETURN_ORDER_CREATED: true,
  RETURN_ORDER_UPDATED: true,
  RETURN_ORDER_DELETED: true,
  CYCLE_COUNT_CREATED: true,
  CYCLE_COUNT_UPDATED: true,
  CYCLE_COUNT_DELETED: true,
  CUSTOMER_CREATED: true,
  CUSTOMER_UPDATED: true,
  CUSTOMER_DELETED: true,

  // --- Organizations ---
  ORG_CREATED: true,
  ORG_MEMBER_INVITED: true,
  ORG_DELETED: true,

  // --- Future / Placeholders (off by default) ---
  VERSION_CREATED: false,
  VERSION_DELETED: false,
  MEMBER_INVITED: false,
  MEMBER_REMOVED: false,
  MEMBER_ROLE_CHANGED: false,
};

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const REACT_APP_CONFIRMATION_EMAIL_REDIRECT =
  process.env.REACT_APP_CONFIRMATION_EMAIL_REDIRECT ||
  'https://replan360.com/home';

const getCurrentAuthEmailLanguage = () => {
  const language = i18n.resolvedLanguage || i18n.language || 'en';
  return String(language).toLowerCase().split('-')[0];
};

/**
 * Firestore `set()` with `{ merge: true }` performs a DEEP merge of maps: any
 * key present in the stored document but absent from the update is kept. That
 * would resurrect params the user deleted in the editor. Walk the stored
 * params and mark every removed key (recursively, e.g. inside `Groups`) with
 * `FieldValue.delete()` so deletions actually persist, while keys present in
 * the update are merged as usual.
 */
const buildMergedParams = (existingParams, newParams) => {
  const merged = { ...(newParams || {}) };
  Object.keys(existingParams || {}).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(newParams || {}, key)) {
      // Removed from the editor — explicitly delete from the stored doc.
      merged[key] = app.firestore.FieldValue.delete();
    } else {
      const storedValue = existingParams[key];
      const newValue = (newParams || {})[key];
      const bothMaps =
        storedValue &&
        newValue &&
        typeof storedValue === 'object' &&
        !Array.isArray(storedValue) &&
        typeof newValue === 'object' &&
        !Array.isArray(newValue);
      if (bothMaps) {
        // Recurse into nested maps (arrays like `order` are replaced wholesale).
        merged[key] = buildMergedParams(storedValue, newValue);
      }
    }
  });
  return merged;
};

class Firebase {
  constructor() {
    app.initializeApp(config);

    /* Helper */
    this.serverValue = app.database.ServerValue;
    this.emailAuthProvider = app.auth.EmailAuthProvider;

    /* Firebase APIs */
    this.auth = app.auth();
    this.rtdb = app.database();
    this.fsdb = app.firestore();

    /* Social Sign In Method Provider */
    this.googleProvider = new app.auth.GoogleAuthProvider();
    this.microsoftProvider = new app.auth.OAuthProvider(
      'microsoft.com',
    );
    // this.facebookProvider = new app.auth.FacebookAuthProvider();
    // this.twitterProvider = new app.auth.TwitterAuthProvider();

    /* analytics */
    this.analytics = app.analytics();
    if (this.analytics) {
      this.analytics.setAnalyticsCollectionEnabled(true);
    }
  }

  // *** Auth API ***

  doCreateUserWithEmailAndPassword = (email, password) =>
    this.auth.createUserWithEmailAndPassword(email, password);

  doApplyActionCode = async (code) => {
    const result = await this.auth.applyActionCode(code);
    if (this.auth.currentUser) {
      await this.auth.currentUser.reload();
      await this.auth.currentUser.getIdToken(true);
    }
    return result;
  };

  doVerifyPasswordResetCode = (code) =>
    this.auth.verifyPasswordResetCode(code);

  doConfirmPasswordReset = (code, newPassword) =>
    this.auth.confirmPasswordReset(code, newPassword);

  doSignInWithEmailAndPassword = async (
    email,
    password,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.auth.signInWithEmailAndPassword(
      email,
      password,
    );

    try {
      // Fetch user's organizations after successful sign-in
      const snapshot = await this.user(result.user.uid).once('value');
      const dbUser = snapshot.val();
      const roles = dbUser?.roles || {};
      const organizations = Object.keys(roles).filter(k => roles[k] !== null);

      // Log sign-in to all organizations the user belongs to
      const logPromises = organizations.map(org =>
        this.maybeLogOrgActivity(org, {
          action: 'USER_SIGNED_IN',
          actorUid: result.user?.uid,
          actorEmail: email,
          details: { provider: 'email' },
        })
      );

      await Promise.all(logPromises);
    } catch (error) {
      console.error('Error logging sign-in activity:', error);
      // Continue with sign-in even if logging fails
    }

    return result;
  };

  doSignInWithGoogle = async (organization = null) => {
    const result = await this.auth.signInWithPopup(
      this.googleProvider,
    );

    try {
      // Fetch user's organizations after successful sign-in
      const snapshot = await this.user(result.user.uid).once('value');
      const dbUser = snapshot.val();

      // Only log if user exists in RTDB and has organizations
      if (dbUser && dbUser.roles) {
        const roles = dbUser.roles;
        const organizations = Object.keys(roles).filter(k => roles[k] !== null);

        // Log sign-in to all organizations the user belongs to
        if (organizations.length > 0) {
          const logPromises = organizations.map(org =>
            this.maybeLogOrgActivity(org, {
              action: 'USER_SIGNED_IN_GOOGLE',
              actorUid: result.user?.uid,
              actorEmail: result.user?.email,
              details: { provider: 'google' },
            })
          );
          await Promise.all(logPromises);
        }
      }
    } catch (error) {
      console.error('Error logging Google sign-in activity:', error);
      // Continue with sign-in even if logging fails
    }

    return result;
  };

  doSignInWithMicrosoft = async (organization = null) => {
    const result = await this.auth.signInWithPopup(
      this.microsoftProvider,
    );

    try {
      // Fetch user's organizations after successful sign-in
      const snapshot = await this.user(result.user.uid).once('value');
      const dbUser = snapshot.val();

      // Only log if user exists in RTDB and has organizations
      if (dbUser && dbUser.roles) {
        const roles = dbUser.roles;
        const organizations = Object.keys(roles).filter(k => roles[k] !== null);

        // Log sign-in to all organizations the user belongs to
        if (organizations.length > 0) {
          const logPromises = organizations.map(org =>
            this.maybeLogOrgActivity(org, {
              action: 'USER_SIGNED_IN_MICROSOFT',
              actorUid: result.user?.uid,
              actorEmail: result.user?.email,
              details: { provider: 'microsoft' },
            })
          );
          await Promise.all(logPromises);
        }
      }
    } catch (error) {
      console.error('Error logging Microsoft sign-in activity:', error);
      // Continue with sign-in even if logging fails
    }

    return result;
  };

  doSignInWithFacebook = () =>
    this.auth.signInWithPopup(this.facebookProvider);

  doSignInWithTwitter = () =>
    this.auth.signInWithPopup(this.twitterProvider);

  doSignOut = async () => {
    const currentUser = this.auth.currentUser;
    if (!currentUser) return this.auth.signOut();

    const currentActorUid = currentUser.uid;
    const currentActorEmail = currentUser.email;

    try {
      // Get user's organizations before signing out
      const snapshot = await this.user(currentActorUid).once('value');
      const dbUser = snapshot.val();
      const roles = dbUser?.roles || {};
      const organizations = Object.keys(roles).filter(k => roles[k] !== null);

      // Log sign-out to all organizations the user belongs to
      const logPromises = organizations.map(org =>
        this.maybeLogOrgActivity(org, {
          action: 'USER_SIGNED_OUT',
          actorUid: currentActorUid,
          actorEmail: currentActorEmail,
          details: {},
        })
      );

      await Promise.all(logPromises);
    } catch (error) {
      console.error('Error logging sign-out activity:', error);
      // Continue with sign-out even if logging fails
    }

    return this.auth.signOut();
  };

  buildAuthEmailPayload = (email, displayName = null) => ({
    email,
    displayName,
    continueUrl: REACT_APP_CONFIRMATION_EMAIL_REDIRECT,
    lang: getCurrentAuthEmailLanguage(),
  });

  doPasswordReset = async (
    email,
    organization = null,
    actorUid = null,
  ) => {
    const payload = this.buildAuthEmailPayload(email);
    let result;
    let delivery = 'backend';

    try {
      result = await authEmailApi.sendPasswordResetEmail(payload);
    } catch (error) {
      if (!error?.shouldFallback) {
        throw error;
      }

      result = await this.auth.sendPasswordResetEmail(email);
      delivery = 'firebase-fallback';
    }

    await this.maybeLogOrgActivity(organization, {
      action: 'USER_PASSWORD_RESET',
      actorUid,
      actorEmail: email,
      details: { delivery },
    });
    return result;
  };

  doSendEmailVerification = async (organization = null) => {
    const currentUser = this.auth.currentUser;
    if (!currentUser?.email) {
      throw new Error('No authenticated user email is available for verification.');
    }

    const payload = this.buildAuthEmailPayload(
      currentUser.email,
      currentUser.displayName,
    );
    let result;
    let delivery = 'backend';

    try {
      result = await authEmailApi.sendVerificationEmail({
        ...payload,
        idToken: await currentUser.getIdToken(),
      });
    } catch (error) {
      if (error?.status === 401) {
        try {
          result = await authEmailApi.sendVerificationEmail({
            ...payload,
            idToken: await currentUser.getIdToken(true),
          });
        } catch (retryError) {
          if (!retryError?.shouldFallback) {
            throw retryError;
          }

          result = await currentUser.sendEmailVerification({
            url: REACT_APP_CONFIRMATION_EMAIL_REDIRECT,
          });
          delivery = 'firebase-fallback';
        }
      } else {
        if (!error?.shouldFallback) {
          throw error;
        }

        result = await currentUser.sendEmailVerification({
          url: REACT_APP_CONFIRMATION_EMAIL_REDIRECT,
        });
        delivery = 'firebase-fallback';
      }
    }

    await this.maybeLogOrgActivity(organization, {
      action: 'USER_EMAIL_VERIFICATION_SENT',
      actorUid: currentUser.uid,
      actorEmail: currentUser.email,
      details: { delivery },
    });
    return result;
  };

  doPasswordUpdate = async (password, organization = null) => {
    const result =
      await this.auth.currentUser.updatePassword(password);
    await this.maybeLogOrgActivity(organization, {
      action: 'USER_PASSWORD_UPDATED',
      actorUid: this.auth.currentUser?.uid,
      actorEmail: this.auth.currentUser?.email,
      details: {},
    });
    return result;
  };

  // *** Merge Auth and DB User API ***

  onAuthUserListener = (next, fallback) =>
    this.auth.onIdTokenChanged((authUser) => {
      if (authUser) {
        // Derive sign-in provider from Firebase Auth providerData
        const providerIds = (authUser.providerData || []).map(p => p.providerId);
        const signInProvider = providerIds.includes('google.com') ? 'google'
          : providerIds.includes('microsoft.com') ? 'microsoft'
            : 'password';

        // Sync emailVerified + signInProvider to RTDB for admin dashboard visibility
        this.user(authUser.uid).update({
          emailVerified: authUser.emailVerified,
          signInProvider,
        }).catch(() => { /* Silent catch to prevent overlay if websocket lags */ });

        // We add a small retry mechanism because immediately after login, the active
        // WebSocket connection might not have fully propagated the auth token yet,
        // resulting in a temporary 'permission_denied'.
        const fetchUserWithRetry = (retries = 3, delay = 500) => {
          // Read only the fields this listener needs (username + roles) instead
          // of the whole users/{uid} node, which contains every preference and
          // Replan draft payload. Saves RTDB download quota on auth/token refresh.
          Promise.all([
            this.user(authUser.uid).child('username').once('value'),
            this.user(authUser.uid).child('roles').once('value'),
          ])
            .then(([usernameSnap, rolesSnap]) => {
              const finalAuthUser = {
                uid: authUser.uid,
                email: authUser.email,
                emailVerified: authUser.emailVerified,
                providerData: authUser.providerData,
                username: usernameSnap.val(),
                roles: rolesSnap.val() || {},
              };
              next(finalAuthUser);
            })
            .catch((error) => {
              if (retries > 0) {
                setTimeout(() => fetchUserWithRetry(retries - 1, delay * 2), delay);
              } else {
                console.error('Failed to fetch user data after retries:', error);
                // Fallback to minimal user if DB fails completely
                next({
                  uid: authUser.uid,
                  email: authUser.email,
                  emailVerified: authUser.emailVerified,
                  providerData: authUser.providerData,
                  roles: {},
                });
              }
            });
        };

        fetchUserWithRetry();
      } else {
        fallback();
      }
    });

  // *** User API ***

  user = (uid) => this.rtdb.ref(`users/${uid}`);

  users = () => this.rtdb.ref('users');

  doCreateOrganization = async (orgName, options = {}) => {
    const trimmed = (orgName || '').trim();
    if (!trimmed) throw new Error('Organization name is required');
    const uid = this.auth.currentUser?.uid;
    if (!uid) throw new Error('Not signed in');
    const idToken = await this.auth.currentUser.getIdToken();
    await orgApi.createOrganization({
      idToken,
      organization: trimmed,
      contractorType: options.contractorType,
      trade: options.trade,
      companySize: options.companySize,
      country: options.country,
      website: options.website,
    });
    await this.auth.currentUser.getIdToken(true);
    await this.maybeLogOrgActivity(trimmed, {
      action: 'ORG_CREATED',
      actorUid: uid,
      actorEmail: this.auth.currentUser?.email,
    });
    return trimmed;
  };

  doUpdateOrganization = async (orgName, options = {}) => {
    const trimmed = (orgName || '').trim();
    if (!trimmed) throw new Error('Organization name is required');
    if (!this.auth.currentUser) throw new Error('Not signed in');
    const idToken = await this.auth.currentUser.getIdToken();
    return orgApi.updateOrganization({
      idToken,
      organization: trimmed,
      contractorType: options.contractorType,
      trade: options.trade,
      companySize: options.companySize,
      country: options.country,
      website: options.website,
    });
  };

  doGetOrgInfo = async (orgName) => {
    const trimmed = (orgName || '').trim();
    if (!trimmed) throw new Error('Organization name is required');
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('Not signed in');
    const idToken = await currentUser.getIdToken();
    return orgApi.getOrgInfo({ idToken, organization: trimmed });
  };

  doInviteUserToOrganization = async (orgName, email, role = ROLE_USER) => {
    const trimmedOrg = (orgName || '').trim();
    if (!trimmedOrg) throw new Error('Organization name is required');
    const validRoles = [ROLE_ADMIN, ROLE_USER, ROLE_GUEST];
    if (role && !validRoles.includes(role)) throw new Error('Invalid role');
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('Not signed in');
    const idToken = await currentUser.getIdToken();
    const result = await orgApi.inviteMember({
      idToken,
      organization: trimmedOrg,
      email: (email || '').trim(),
      role,
    });
    await this.maybeLogOrgActivity(trimmedOrg, {
      action: 'ORG_MEMBER_INVITED',
      actorUid: currentUser.uid,
      actorEmail: currentUser.email,
      details: { invitedEmail: (email || '').trim().toLowerCase(), invitedRole: role },
    });
    return result;
  };

  doGetOrgMembers = async (orgName) => {
    const trimmedOrg = (orgName || '').trim();
    if (!trimmedOrg) return [];
    const snapshot = await this.rtdb.ref(`orgMembers/${trimmedOrg}`).once('value');
    const data = snapshot.val();
    if (!data) return [];
    return Object.entries(data).map(([uid, member]) => ({
      uid,
      username: member?.username || '',
      email: member?.email || '',
      emailVerified: Boolean(member?.emailVerified),
      role: member?.role,
    }));
  };

  doDeleteOrganization = async (orgName) => {
    const trimmedOrg = (orgName || '').trim();
    if (!trimmedOrg) throw new Error('Organization name is required');
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('Not signed in');
    const idToken = await currentUser.getIdToken();
    await orgApi.deleteOrganization({ idToken, organization: trimmedOrg });
    await currentUser.getIdToken(true);
    await this.maybeLogOrgActivity(trimmedOrg, {
      action: 'ORG_DELETED',
      actorUid: currentUser.uid,
      actorEmail: currentUser.email,
      details: {},
    });
    return trimmedOrg;
  };

  doGetMyOrgInvites = async () => {
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('Not signed in');
    const idToken = await currentUser.getIdToken();
    return orgApi.getMyInvites({ idToken });
  };

  doRespondToOrgInvite = async (inviteId, accept) => {
    if (!inviteId) throw new Error('Invite ID is required');
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('Not signed in');
    const idToken = await currentUser.getIdToken();
    const result = await orgApi.respondToInvite({
      idToken,
      inviteId,
      accept: !!accept,
    });
    if (accept) {
      await currentUser.getIdToken(true);
    }
    return result;
  };

  doGetOrgInviteByToken = async (inviteId) => {
    if (!inviteId) return null;
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('Not signed in');
    const idToken = await currentUser.getIdToken();
    return orgApi.getInviteByToken({ idToken, inviteId });
  };

  doGetOrgInviteStatus = async (orgName) => {
    const trimmedOrg = (orgName || '').trim();
    if (!trimmedOrg) return [];
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('Not signed in');
    const idToken = await currentUser.getIdToken();
    return orgApi.getOrgInviteStatus({ idToken, organization: trimmedOrg });
  };

  // *** Message API ***

  message = (uid) => this.rtdb.ref(`messages/${uid}`);

  messages = () => this.rtdb.ref('messages');

  onMessagesFeed = (callback, limit = 8) => {
    const ref = this.messages().orderByChild('createdAt').limitToLast(limit);
    const handler = (snapshot) => {
      const obj = snapshot.val();
      if (!obj) return callback([]);
      const items = Object.keys(obj)
        .map(k => ({ ...obj[k], uid: k }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      callback(items);
    };
    ref.on('value', handler);
    return { ref, handler };
  };

  offMessagesFeed = ({ ref, handler }) => {
    if (ref && handler) ref.off('value', handler);
  };

  // *** Org Activity Log API (RTDB) ***

  orgLog = (organization) => this.rtdb.ref(`orgLogs/${organization}`);

  // Core logging method — checks LOGGABLE_EVENTS before writing.
  // Silently skips if organization or actorUid is missing, or if the event is disabled.
  maybeLogOrgActivity = (
    organization,
    { action, actorUid, actorEmail = null, details = {} },
  ) => {
    if (!organization || !actorUid) return Promise.resolve();
    if (!LOGGABLE_EVENTS[action]) return Promise.resolve();
    return this.orgLog(organization).push({
      action,
      actorUid,
      actorEmail,
      details,
      timestamp: this.serverValue.TIMESTAMP,
    });
  };

  // Real-time listener for an activity feed UI component.
  // Uses limitToLast at Firebase level to cap bandwidth, then sorts in-memory
  onOrgActivityFeed = (organization, callback, limit = 50) => {
    // Cap at 200 max to prevent excessive Firebase reads
    const firebaseLimit = Math.min(limit * 2, 200);
    const ref = this.orgLog(organization).limitToLast(firebaseLimit);
    return ref.on('value', (snapshot) => {
      const logs = [];
      snapshot.forEach((child) => {
        logs.push({ id: child.key, ...child.val() });
      });
      // Sort by timestamp (newest first) and limit in-memory
      logs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      callback(logs.slice(0, limit));
    });
  };

  offOrgActivityFeed = (organization, limit = 50) => {
    // Must use the SAME query ref that was registered in onOrgActivityFeed.
    // Calling .off() on the base ref does NOT detach query listeners.
    const firebaseLimit = Math.min(limit * 2, 200);
    this.orgLog(organization).limitToLast(firebaseLimit).off('value');
  };

  // Real-time listener for activity scoped to a single project within an org.
  // Filters at the RTDB level via orderByChild('details/projectName').equalTo(projectName).
  onProjectActivityFeed = (organization, projectName, callback, limit = 50) => {
    if (!projectName) {
      callback([]);
      return () => {};
    }
    const firebaseLimit = Math.min(limit * 2, 200);
    const ref = this.orgLog(organization)
      .orderByChild('details/projectName')
      .equalTo(projectName)
      .limitToLast(firebaseLimit);
    const handler = (snapshot) => {
      const logs = [];
      snapshot.forEach((child) => {
        logs.push({ id: child.key, ...child.val() });
      });
      logs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      callback(logs.slice(0, limit));
    };
    ref.on('value', handler);
    return { ref, handler };
  };

  offProjectActivityFeed = ({ ref, handler } = {}) => {
    if (ref && handler) ref.off('value', handler);
  };

  doGetOrgActivityLog = async (organization, limit = 100) => {
    // Cap at 200 max to prevent excessive Firebase reads
    const firebaseLimit = Math.min(limit * 2, 200);
    const snapshot = await this.orgLog(organization)
      .limitToLast(firebaseLimit)
      .once('value');
    const logs = [];
    snapshot.forEach((child) =>
      logs.push({ id: child.key, ...child.val() }),
    );
    // Sort by timestamp (newest first) and limit in-memory
    logs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    return logs.slice(0, limit);
  };

  doGetOrgActivityLogPaginated = async (organization, limit = 8, endBefore = null) => {
    let query = this.orgLog(organization).orderByChild('timestamp');
    if (endBefore != null) {
      query = query.endBefore(endBefore);
    }
    const firebaseLimit = Math.min(limit * 2, 200);
    const snapshot = await query.limitToLast(firebaseLimit).once('value');
    const logs = [];
    snapshot.forEach((child) =>
      logs.push({ id: child.key, ...child.val() }),
    );
    logs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    return logs.slice(0, limit);
  };

  doGetOrgActivityLogFiltered = async (organization, { action, projectName, limit = 50 } = {}) => {
    let ref = this.orgLog(organization);

    if (projectName) {
      ref = ref.orderByChild('details/projectName').equalTo(projectName);
    } else if (action) {
      ref = ref.orderByChild('action').equalTo(action);
    } else {
      ref = ref.orderByChild('timestamp');
    }

    ref = ref.limitToLast(Math.min(limit * 2, 200));

    const snapshot = await ref.once('value');
    const logs = [];
    snapshot.forEach((child) =>
      logs.push({ id: child.key, ...child.val() }),
    );
    logs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    return logs.slice(0, limit);
  };

  // *** User Preferences API ***

  // Helper to encode path segments for Firebase RTDB (doesn't allow . # $ [ ] in keys)
  encodeFirebasePath = (path) => {
    if (!path) return path;
    // Split by / and encode each segment to handle special chars in project names, etc.
    // encodeURIComponent doesn't encode periods, but Firebase doesn't allow them
    return path.split('/').map(segment =>
      encodeURIComponent(segment).replace(/\./g, '%2E')
    ).join('/');
  };

  doSetUserPreference = (uid, path, value) =>
    this.rtdb.ref(`users/${uid}/preferences/${this.encodeFirebasePath(path)}`).set(value);

  doGetUserPreference = async (uid, path) => {
    const snapshot = await this.rtdb
      .ref(`users/${uid}/preferences/${this.encodeFirebasePath(path)}`)
      .once('value');
    return snapshot.val();
  };

  doRemoveUserPreference = (uid, path) =>
    this.rtdb.ref(`users/${uid}/preferences/${this.encodeFirebasePath(path)}`).remove();

  // *** Notification Tracking API ***

  doSetNotificationCheckTime = (uid) =>
    this.doSetUserPreference(uid, 'lastNotificationCheckTime', Date.now());

  doGetNotificationCheckTime = async (uid) => {
    const timestamp = await this.doGetUserPreference(uid, 'lastNotificationCheckTime');
    return timestamp || 0; // Default to 0 (epoch) if never checked
  };

  // *** CURRENCY CONVERTER API ***

  rate = (timestamp) => this.fsdb.collection('rates').doc(timestamp);
  rates = () => this.fsdb.collection('rates');
  timeserie = (code) => this.fsdb.collection('rates').doc(code);
  historicalRates = (datelist) =>
    this.fsdb
      .collection('historical_rates')
      .where(app.firestore.FieldPath.documentId(), 'in', datelist);

  // *** Drawings API (Firestore) ***

  // Path: /{organization}/{projectName}/versions/{versionName}/pages/{pageIndex}
  pageDoc = (organization, project, version, page) =>
    this.fsdb
      .collection(organization || 'projects')
      .doc(project)
      .collection('versions')
      .doc(version)
      .collection('pages')
      .doc(String(page));

  doSavePageData = async (
    organization,
    project,
    version,
    page,
    pageData,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.pageDoc(
      organization,
      project,
      version,
      page,
    ).set(pageData, {
      merge: true,
    });
    await this.maybeLogOrgActivity(organization, {
      action: 'PAGE_SAVED',
      actorUid,
      actorEmail,
      details: { projectName: project, version, page },
    });
    return result;
  };

  // Patches page data and updates only relevant shapes by id.
  // Supports partial writes:
  //   upserts: { [shapeId]: shapeData }
  //   deletedIds: array of shapeIds to remove
  //   shapeOrder: array of shapeIds for draw order
  //   pageFields: page-level fields (measurementSystem, selectedScaleKey, etc.)
  //   fieldsToDelete: field paths to remove (e.g. legacy migration)
  //   actorUid / actorEmail: for activity logging
  doPatchPageData = async (
    organization,
    project,
    version,
    page,
    {
      upserts = {},
      deletedIds = [],
      shapeOrder = null,
      pageFields = {},
      fieldsToDelete = [],
      actorUid = null,
      actorEmail = null,
    } = {},
  ) => {
    const pageRef = this.pageDoc(
      organization,
      project,
      version,
      page,
    );
    const snapshot = await pageRef.get();

    let result;

    if (!snapshot.exists) {
      const initialData = {
        ...pageFields,
        updatedAt: app.firestore.FieldValue.serverTimestamp(),
        shapesById: { ...upserts },
      };
      if (Array.isArray(shapeOrder)) {
        initialData.shapeOrder = shapeOrder;
      }
      result = await pageRef.set(initialData, { merge: true });
    } else {
      const updates = {
        ...pageFields,
        updatedAt: app.firestore.FieldValue.serverTimestamp(),
      };
      if (Array.isArray(shapeOrder)) {
        updates.shapeOrder = shapeOrder;
      }
      Object.entries(upserts).forEach(([shapeId, shapeData]) => {
        updates[`shapesById.${shapeId}`] = shapeData;
      });
      deletedIds.forEach((shapeId) => {
        updates[`shapesById.${shapeId}`] =
          app.firestore.FieldValue.delete();
      });
      fieldsToDelete.forEach((fieldPath) => {
        updates[fieldPath] = app.firestore.FieldValue.delete();
      });
      result = await pageRef.update(updates);
    }

    await this.maybeLogOrgActivity(organization, {
      action: 'PAGE_ANNOTATED',
      actorUid: pageFields?.updatedBy || actorUid,
      actorEmail: pageFields?.updatedByEmail || actorEmail,
      details: {
        projectName: project,
        version,
        page,
        shapesUpserted: Object.keys(upserts).length,
        shapesDeleted: deletedIds.length,
      },
    });

    return result;
  };

  doGetPageData = (organization, project, version, page) =>
    this.pageDoc(organization, project, version, page).get();

  doGetPagesWithShapes = async (organization, project, version) => {
    const pagesCollection = this.fsdb
      .collection(organization || 'projects')
      .doc(project)
      .collection('versions')
      .doc(version)
      .collection('pages');
    const snapshot = await pagesCollection.get();
    const pageNumbers = new Set();
    snapshot.forEach((doc) => {
      pageNumbers.add(parseInt(doc.id, 10));
    });
    return pageNumbers;
  };

  doGetAllPagesWithShapes = async (
    organization,
    project,
    versions,
  ) => {
    const result = new Map();
    if (!versions || versions.length === 0) return result;
    const promises = versions.map(async (versionName) => {
      try {
        const pagesCollection = this.fsdb
          .collection(organization || 'projects')
          .doc(project)
          .collection('versions')
          .doc(versionName)
          .collection('pages');
        const pagesSnapshot = await pagesCollection.get();
        const pageNumbers = new Set();
        pagesSnapshot.forEach((pageDoc) => {
          pageNumbers.add(parseInt(pageDoc.id, 10));
        });
        return { versionName, pageNumbers };
      } catch (err) {
        console.error(
          `Error fetching pages for version ${versionName}:`,
          err,
        );
        return { versionName, pageNumbers: new Set() };
      }
    });
    const results = await Promise.all(promises);
    results.forEach(({ versionName, pageNumbers }) => {
      if (pageNumbers.size > 0) result.set(versionName, pageNumbers);
    });
    return result;
  };

  // *** Projects API (Firestore) ***

  doGetProjectVersions = async (organization, project) => {
    try {
      const snapshot = await this.fsdb
        .collection(organization || 'projects')
        .doc(project)
        .collection('versions')
        .get();
      return snapshot.docs.map(doc => doc.id);
    } catch (error) {
      console.error('Error fetching project versions:', error);
      return [];
    }
  };

  doGetOrganizationProjects = async (organization) => {
    try {
      const snapshot = await this.fsdb
        .collection(organization || 'projects')
        .get();
      const projects = [];
      snapshot.forEach((doc) => {
        projects.push({ id: doc.id, name: doc.id, ...doc.data() });
      });
      return projects;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  };

  doCreateProject = async (
    organization,
    projectName,
    projectData = {},
    actorUid,
    actorEmail,
  ) => {
    try {
      await this.fsdb
        .collection(organization || 'projects')
        .doc(projectName)
        .set({
          name: projectName,
          createdAt: app.firestore.FieldValue.serverTimestamp(),
          ...projectData,
        });
      await this.maybeLogOrgActivity(organization, {
        action: 'PROJECT_CREATED',
        actorUid,
        actorEmail,
        details: { projectName },
      });
      return projectName;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  };

  doUpdateProject = async (
    organization,
    oldProjectName,
    newProjectName,
    customFields = {},
    updatedBy = null,
    fieldsToDelete = [],
    projectFolders = [],
  ) => {
    try {
      const oldProjectRef = this.fsdb
        .collection(organization || 'projects')
        .doc(oldProjectName);
      const oldProjectDoc = await oldProjectRef.get();
      if (!oldProjectDoc.exists) {
        throw new Error(`Project "${oldProjectName}" does not exist`);
      }
      const existingData = oldProjectDoc.data();
      const nameChanged = oldProjectName !== newProjectName;
      const updatedData = {
        name: newProjectName,
        createdAt:
          existingData.createdAt ||
          app.firestore.FieldValue.serverTimestamp(),
        createdBy: existingData.createdBy || 'Unknown User',
        updatedAt: app.firestore.FieldValue.serverTimestamp(),
        updatedBy: updatedBy || 'Unknown User',
        projectFolders: projectFolders || [],
        ...customFields,
      };
      fieldsToDelete.forEach((fieldKey) => {
        updatedData[fieldKey] = app.firestore.FieldValue.delete();
      });

      if (nameChanged) {
        const newProjectRef = this.fsdb
          .collection(organization || 'projects')
          .doc(newProjectName);
        await newProjectRef.set(updatedData);
        const versionsSnapshot = await oldProjectRef
          .collection('versions')
          .get();
        for (const versionDoc of versionsSnapshot.docs) {
          await newProjectRef
            .collection('versions')
            .doc(versionDoc.id)
            .set(versionDoc.data());
          const pagesSnapshot = await oldProjectRef
            .collection('versions')
            .doc(versionDoc.id)
            .collection('pages')
            .get();
          for (const pageDoc of pagesSnapshot.docs) {
            await newProjectRef
              .collection('versions')
              .doc(versionDoc.id)
              .collection('pages')
              .doc(pageDoc.id)
              .set(pageDoc.data());
          }
        }
        const assembliesSnapshot = await oldProjectRef
          .collection('assemblies')
          .get();
        for (const assemblyDoc of assembliesSnapshot.docs) {
          await newProjectRef
            .collection('assemblies')
            .doc(assemblyDoc.id)
            .set(assemblyDoc.data());
        }
        for (const versionDoc of versionsSnapshot.docs) {
          const pagesSnapshot = await oldProjectRef
            .collection('versions')
            .doc(versionDoc.id)
            .collection('pages')
            .get();
          for (const pageDoc of pagesSnapshot.docs)
            await pageDoc.ref.delete();
          await versionDoc.ref.delete();
        }
        for (const assemblyDoc of assembliesSnapshot.docs)
          await assemblyDoc.ref.delete();
        await oldProjectRef.delete();

        await this.maybeLogOrgActivity(organization, {
          action: 'PROJECT_RENAMED',
          actorUid: this.auth.currentUser?.uid,
          actorEmail: this.auth.currentUser?.email,
          details: { oldProjectName, newProjectName },
        });
        return newProjectName;
      } else {
        const updateData = {
          name: newProjectName,
          updatedAt: app.firestore.FieldValue.serverTimestamp(),
          updatedBy: this.auth.currentUser?.email,
          projectFolders: projectFolders || [],
          ...customFields,
        };
        fieldsToDelete.forEach((fieldKey) => {
          updateData[fieldKey] = app.firestore.FieldValue.delete();
        });
        await oldProjectRef.update(updateData);

        await this.maybeLogOrgActivity(organization, {
          action: 'PROJECT_UPDATED',
          actorUid: this.auth.currentUser?.uid,
          actorEmail: this.auth.currentUser?.email,
          details: {
            projectName: newProjectName,
            customFields: Object.keys(customFields),
          },
        });
        return oldProjectName;
      }
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  };

  doUpdateProjectName = async (
    organization,
    oldProjectName,
    newProjectName,
  ) =>
    this.doUpdateProject(
      organization,
      oldProjectName,
      newProjectName,
      {},
    );

  doDeleteProject = async (
    organization,
    projectName,
    actorUid = null,
    actorEmail = null,
  ) => {
    try {
      const projectRef = this.fsdb
        .collection(organization || 'projects')
        .doc(projectName);
      const projectDoc = await projectRef.get();
      if (!projectDoc.exists) {
        throw new Error(`Project "${projectName}" does not exist`);
      }
      const versionsSnapshot = await projectRef
        .collection('versions')
        .get();
      for (const versionDoc of versionsSnapshot.docs) {
        const pagesSnapshot = await projectRef
          .collection('versions')
          .doc(versionDoc.id)
          .collection('pages')
          .get();
        for (const pageDoc of pagesSnapshot.docs)
          await pageDoc.ref.delete();
        await versionDoc.ref.delete();
      }
      const assembliesSnapshot = await projectRef
        .collection('assemblies')
        .get();
      for (const assemblyDoc of assembliesSnapshot.docs)
        await assemblyDoc.ref.delete();
      await projectRef.delete();

      await this.maybeLogOrgActivity(organization, {
        action: 'PROJECT_DELETED',
        actorUid,
        actorEmail,
        details: { projectName },
      });
      return true;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  };

  // *** Planning / Gantt API (Firestore) ***

  // Path: /{organization}/{projectName}/planning/tasks
  planningDoc = (organization, project, actorUid = null, actorEmail = null) =>
    this.fsdb
      .collection(organization || 'projects')
      .doc(project)
      .collection('planning')
      .doc('tasks');

  doSavePlanningTasks = async (
    organization,
    project,
    tasks,
    actorUid = null,
    actorEmail = null,
  ) => {
    try {
      await this.planningDoc(organization, project).set(
        {
          tasks,
          updatedAt: app.firestore.FieldValue.serverTimestamp(),
        },
        { merge: false },
      );
      await this.maybeLogOrgActivity(organization, {
        action: 'PLANNING_SAVED',
        actorUid: actorUid,
        actorEmail: actorEmail,
        details: { projectName: project, taskCount: tasks.length },
      });
    } catch (error) {
      console.error('Error saving planning tasks:', error);
      throw error;
    }
  };

  doGetPlanningTasks = async (organization, project) => {
    try {
      const doc = await this.planningDoc(organization, project).get();
      return doc.exists ? doc.data() : null;
    } catch (error) {
      console.error('Error fetching planning tasks:', error);
      throw error;
    }
  };

  doSavePlanningBaseline = async (organization, project, tasks) => {
    try {
      return await this.planningDoc(organization, project).set(
        {
          baseline: tasks,
          baselineUpdatedAt: app.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error('Error saving planning baseline:', error);
      throw error;
    }
  };

  onPlanningTasksChange = (organization, project, callback) =>
    this.planningDoc(organization, project).onSnapshot(
      (doc) => {
        if (doc.exists) callback(doc.data());
      },
      (error) => {
        console.error('Error listening to planning tasks:', error);
      },
    );

  // *** Assemblies API (Firestore) ***

  /**
   * Normalize an assembly name to a safe document ID.
   * - Trims whitespace
   * - Replaces chars not in [A-Za-z0-9 _-] with spaces
   * - Collapses and trims whitespace, replaces spaces with underscores
   * - Collapses consecutive hyphens/underscores, removes leading/trailing
   * - Enforces max 100 chars
   * - Returns empty string if result is empty, '.', or '..'
   */
  normalizeAssemblyId = (name) => {
    if (!name || typeof name !== 'string') return '';
    let normalized = name.trim();
    normalized = normalized.replace(/\//g, '_');
    normalized = normalized.replace(/_+/g, '_');
    normalized = normalized.replace(/^[-_]+|[-_]+$/g, '');
    if (!normalized || normalized === '.' || normalized === '..') return '';
    if (normalized.length > 100) {
      normalized = normalized.substring(0, 100);
      normalized = normalized.replace(/[-_]+$/g, '');
    }
    return normalized;
  };

  /**
   * Returns an error string if the name is invalid, or null if valid.
   */
  assemblyNameError = (name) => {
    if (!name || !name.trim()) return 'Assembly name cannot be empty.';
    const normalized = this.normalizeAssemblyId(name);
    if (!normalized) {
      if (name.trim() === '.' || name.trim() === '..') {
        return 'Assembly name cannot be "." or "..".';
      }
      return 'Assembly name contains invalid characters or is empty after normalization.';
    }
    return null;
  };

  assembliesCollection = (organization, project) =>
    this.fsdb
      .collection(organization || 'projects')
      .doc(project)
      .collection('assemblies');

  assemblyDoc = (organization, project, assemblyId) =>
    this.assembliesCollection(organization, project).doc(assemblyId);

  doCreateAssembly = async (
    organization,
    project,
    assemblyId,
    params,
    name = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const normalizedId = this.normalizeAssemblyId(assemblyId);
    if (!normalizedId) {
      const err = new Error(
        this.assemblyNameError(assemblyId) || 'Invalid assembly name.',
      );
      err.code = 'INVALID_ASSEMBLY_NAME';
      throw err;
    }
    const docRef = this.assemblyDoc(organization, project, normalizedId);
    // Use runTransaction for atomic existence check + write (TOCTOU race fix)
    await this.fsdb.runTransaction(async (transaction) => {
      const doc = await transaction.get(docRef);
      if (doc.exists) {
        const err = new Error(
          `Assembly "${normalizedId}" already exists.`,
        );
        err.code = 'ASSEMBLY_ALREADY_EXISTS';
        throw err;
      }
      transaction.set(docRef, {
        name: name || normalizedId,
        params,
      });
    });
    await this.maybeLogOrgActivity(organization, {
      action: 'ASSEMBLY_CREATED',
      actorUid,
      actorEmail,
      details: { projectName: project, assemblyId: normalizedId },
    });
  };

  doGetProjectAssemblies = (organization, project) =>
    this.assembliesCollection(organization, project).get();

  doDeleteAssembly = async (
    organization,
    project,
    assemblyId,
    actorUid = null,
    actorEmail = null,
  ) => {
    await this.assemblyDoc(
      organization,
      project,
      assemblyId,
    ).delete();
    await this.maybeLogOrgActivity(organization, {
      action: 'ASSEMBLY_DELETED',
      actorUid,
      actorEmail,
      details: { projectName: project, assemblyId },
    });
  };

  doUpdateAssembly = async (
    organization,
    project,
    assemblyId,
    params,
    name = null,
    actorUid = null,
    actorEmail = null,
    options = {},
  ) => {
    const { deepDeleteRemoved = false } = options || {};
    const normalizedId = this.normalizeAssemblyId(assemblyId);
    if (!normalizedId) {
      const err = new Error(
        this.assemblyNameError(assemblyId) || 'Invalid assembly name.',
      );
      err.code = 'INVALID_ASSEMBLY_NAME';
      throw err;
    }
    const docRef = this.assemblyDoc(organization, project, normalizedId);
    const existingDoc = await docRef.get();
    if (!existingDoc.exists) {
      const err = new Error(`Assembly "${normalizedId}" not found.`);
      err.code = 'ASSEMBLY_NOT_FOUND';
      throw err;
    }
    // `set(..., { merge: true })` deep-merges and keeps stored keys absent
    // from the update. Only delete removed keys when the caller is writing the
    // assembly's FULL param state (AssemblyManager editor) and opts in via
    // `deepDeleteRemoved`. Partial-param callers (saveAssemblyFromShape,
    // takeoff) must NOT deep-delete or they would wipe unrelated stored params.
    const paramsToWrite = deepDeleteRemoved
      ? buildMergedParams(existingDoc.data()?.params, params)
      : params;
    await docRef.set(
      {
        name: name || normalizedId,
        params: paramsToWrite,
      },
      { merge: true },
    );
    await this.maybeLogOrgActivity(organization, {
      action: 'ASSEMBLY_UPDATED',
      actorUid,
      actorEmail,
      details: { projectName: project, assemblyId: normalizedId },
    });
  };

  doRenameAssembly = async (
    organization,
    project,
    oldAssemblyId,
    newName,
    params = null,
    name = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const normalizedOldId = this.normalizeAssemblyId(oldAssemblyId);
    if (!normalizedOldId) {
      const err = new Error('Invalid old assembly name.');
      err.code = 'INVALID_ASSEMBLY_NAME';
      throw err;
    }
    const newNormalizedId = this.normalizeAssemblyId(newName);
    if (!newNormalizedId) {
      const err = new Error(
        this.assemblyNameError(newName) || 'Invalid new assembly name.',
      );
      err.code = 'INVALID_ASSEMBLY_NAME';
      throw err;
    }

    const oldDocRef = this.assemblyDoc(organization, project, normalizedOldId);
    const oldDoc = await oldDocRef.get();
    if (!oldDoc.exists) {
      const err = new Error(`Assembly "${normalizedOldId}" not found.`);
      err.code = 'ASSEMBLY_NOT_FOUND';
      throw err;
    }

    const oldData = oldDoc.data();

    if (normalizedOldId !== newNormalizedId) {
      const newDocRef = this.assemblyDoc(
        organization,
        project,
        newNormalizedId,
      );
      const newDoc = await newDocRef.get();
      if (newDoc.exists) {
        const err = new Error(
          `Assembly "${newNormalizedId}" already exists.`,
        );
        err.code = 'ASSEMBLY_ALREADY_EXISTS';
        throw err;
      }
    }

    // Use batch for atomic write+delete (rename is not a transaction because we
    // already read the old doc above, but a batch ensures both ops succeed or fail)
    const batch = this.fsdb.batch();
    batch.set(this.assemblyDoc(organization, project, newNormalizedId), {
      name: name || newNormalizedId,
      params: params || oldData.params || {},
    });
    if (normalizedOldId !== newNormalizedId) {
      batch.delete(oldDocRef);
    }
    await batch.commit();

    await this.maybeLogOrgActivity(organization, {
      action: 'ASSEMBLY_RENAMED',
      actorUid,
      actorEmail,
      details: {
        projectName: project,
        oldAssemblyId: normalizedOldId,
        newAssemblyId: newNormalizedId,
      },
    });

    return { oldId: normalizedOldId, newId: newNormalizedId };
  };

  // *** Organization Assemblies API (Firestore) ***

  orgAssembliesCollection = (organization) =>
    this.fsdb
      .collection('organization_assemblies')
      .doc(organization)
      .collection('assemblies');

  orgAssemblyDoc = (organization, assemblyId) =>
    this.orgAssembliesCollection(organization).doc(assemblyId);

  doCreateOrgAssembly = async (
    organization,
    assemblyId,
    params,
    name = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const normalizedId = this.normalizeAssemblyId(assemblyId);
    if (!normalizedId) {
      const err = new Error(
        this.assemblyNameError(assemblyId) || 'Invalid assembly name.',
      );
      err.code = 'INVALID_ASSEMBLY_NAME';
      throw err;
    }
    const docRef = this.orgAssemblyDoc(organization, normalizedId);
    // Use runTransaction for atomic existence check + write (TOCTOU race fix)
    await this.fsdb.runTransaction(async (transaction) => {
      const doc = await transaction.get(docRef);
      if (doc.exists) {
        const err = new Error(
          `Assembly "${normalizedId}" already exists.`,
        );
        err.code = 'ASSEMBLY_ALREADY_EXISTS';
        throw err;
      }
      transaction.set(docRef, {
        name: name || normalizedId,
        params,
      });
    });
    await this.maybeLogOrgActivity(organization, {
      action: 'ORG_ASSEMBLY_CREATED',
      actorUid,
      actorEmail,
      details: { assemblyId: normalizedId },
    });
  };

  doGetOrgAssemblies = (organization) =>
    this.orgAssembliesCollection(organization).get();

  doDeleteOrgAssembly = async (
    organization,
    assemblyId,
    actorUid = null,
    actorEmail = null,
  ) => {
    await this.orgAssemblyDoc(organization, assemblyId).delete();
    await this.maybeLogOrgActivity(organization, {
      action: 'ORG_ASSEMBLY_DELETED',
      actorUid,
      actorEmail,
      details: { assemblyId },
    });
  };

  doUpdateOrgAssembly = async (
    organization,
    assemblyId,
    params,
    name = null,
    actorUid = null,
    actorEmail = null,
    options = {},
  ) => {
    const { deepDeleteRemoved = false } = options || {};
    const normalizedId = this.normalizeAssemblyId(assemblyId);
    if (!normalizedId) {
      const err = new Error(
        this.assemblyNameError(assemblyId) || 'Invalid assembly name.',
      );
      err.code = 'INVALID_ASSEMBLY_NAME';
      throw err;
    }
    const docRef = this.orgAssemblyDoc(organization, normalizedId);
    const existingDoc = await docRef.get();
    if (!existingDoc.exists) {
      const err = new Error(`Assembly "${normalizedId}" not found.`);
      err.code = 'ASSEMBLY_NOT_FOUND';
      throw err;
    }
    // `set(..., { merge: true })` deep-merges and keeps stored keys absent
    // from the update. Only delete removed keys when the caller is writing the
    // assembly's FULL param state (AssemblyManager editor) and opts in via
    // `deepDeleteRemoved`. Partial-param callers (saveAssemblyFromShape,
    // takeoff) must NOT deep-delete or they would wipe unrelated stored params.
    const paramsToWrite = deepDeleteRemoved
      ? buildMergedParams(existingDoc.data()?.params, params)
      : params;
    await docRef.set(
      {
        name: name || normalizedId,
        params: paramsToWrite,
      },
      { merge: true },
    );
    await this.maybeLogOrgActivity(organization, {
      action: 'ASSEMBLY_UPDATED',
      actorUid,
      actorEmail,
      details: { assemblyId: normalizedId },
    });
  };

  doRenameOrgAssembly = async (
    organization,
    oldAssemblyId,
    newName,
    params = null,
    name = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const normalizedOldId = this.normalizeAssemblyId(oldAssemblyId);
    if (!normalizedOldId) {
      const err = new Error('Invalid old assembly name.');
      err.code = 'INVALID_ASSEMBLY_NAME';
      throw err;
    }
    const newNormalizedId = this.normalizeAssemblyId(newName);
    if (!newNormalizedId) {
      const err = new Error(
        this.assemblyNameError(newName) || 'Invalid new assembly name.',
      );
      err.code = 'INVALID_ASSEMBLY_NAME';
      throw err;
    }

    const oldDocRef = this.orgAssemblyDoc(organization, normalizedOldId);
    const oldDoc = await oldDocRef.get();
    if (!oldDoc.exists) {
      const err = new Error(`Assembly "${normalizedOldId}" not found.`);
      err.code = 'ASSEMBLY_NOT_FOUND';
      throw err;
    }

    const oldData = oldDoc.data();

    if (normalizedOldId !== newNormalizedId) {
      const newDocRef = this.orgAssemblyDoc(organization, newNormalizedId);
      const newDoc = await newDocRef.get();
      if (newDoc.exists) {
        const err = new Error(
          `Assembly "${newNormalizedId}" already exists.`,
        );
        err.code = 'ASSEMBLY_ALREADY_EXISTS';
        throw err;
      }
    }

    // Use batch for atomic write+delete
    const batch = this.fsdb.batch();
    batch.set(this.orgAssemblyDoc(organization, newNormalizedId), {
      name: name || newNormalizedId,
      params: params || oldData.params || {},
    });
    if (normalizedOldId !== newNormalizedId) {
      batch.delete(oldDocRef);
    }
    await batch.commit();

    await this.maybeLogOrgActivity(organization, {
      action: 'ASSEMBLY_RENAMED',
      actorUid,
      actorEmail,
      details: {
        oldAssemblyId: normalizedOldId,
        newAssemblyId: newNormalizedId,
      },
    });

    return { oldId: normalizedOldId, newId: newNormalizedId };
  };

  // *** Resource Management API (RTDB - under users branch) ***
  // Pass `organization` as an optional trailing argument to enable activity logging.

  userResourceManagement = (uid) =>
    this.rtdb.ref(`users/${uid}/resourceManagement`);

  userProjects = (uid) =>
    this.rtdb.ref(`users/${uid}/resourceManagement/projects`);

  userProject = (uid, projectId) =>
    this.rtdb.ref(
      `users/${uid}/resourceManagement/projects/${projectId}`,
    );

  doSaveUserProject = async (
    uid,
    projectId,
    projectData,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.userProject(uid, projectId).set(
      projectData,
    );
    await this.maybeLogOrgActivity(organization, {
      action: 'RESOURCE_PROJECT_SAVED',
      actorUid: actorUid || uid,
      actorEmail,
      details: { projectId },
    });
    return result;
  };

  doUpdateUserProject = async (
    uid,
    projectId,
    updates,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.userProject(uid, projectId).update(
      updates,
    );
    await this.maybeLogOrgActivity(organization, {
      action: 'RESOURCE_PROJECT_UPDATED',
      actorUid: actorUid || uid,
      actorEmail,
      details: { projectId },
    });
    return result;
  };

  doDeleteUserProject = async (
    uid,
    projectId,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.userProject(uid, projectId).remove();
    await this.maybeLogOrgActivity(organization, {
      action: 'RESOURCE_PROJECT_DELETED',
      actorUid: actorUid || uid,
      actorEmail,
      details: { projectId },
    });
    return result;
  };

  userResources = (uid) =>
    this.rtdb.ref(`users/${uid}/resourceManagement/resources`);

  userResource = (uid, resourceId) =>
    this.rtdb.ref(
      `users/${uid}/resourceManagement/resources/${resourceId}`,
    );

  doSaveUserResource = async (
    uid,
    resourceId,
    resourceData,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.userResource(uid, resourceId).set(
      resourceData,
    );
    await this.maybeLogOrgActivity(organization, {
      action: 'RESOURCE_SAVED',
      actorUid: actorUid || uid,
      actorEmail,
      details: { resourceId },
    });
    return result;
  };

  doUpdateUserResource = async (
    uid,
    resourceId,
    updates,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.userResource(uid, resourceId).update(
      updates,
    );
    await this.maybeLogOrgActivity(organization, {
      action: 'RESOURCE_UPDATED',
      actorUid: actorUid || uid,
      actorEmail,
      details: { resourceId },
    });
    return result;
  };

  doDeleteUserResource = async (
    uid,
    resourceId,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.userResource(uid, resourceId).remove();
    await this.maybeLogOrgActivity(organization, {
      action: 'RESOURCE_DELETED',
      actorUid: actorUid || uid,
      actorEmail,
      details: { resourceId },
    });
    return result;
  };

  userAssignments = (uid) =>
    this.rtdb.ref(`users/${uid}/resourceManagement/assignments`);

  userAssignment = (uid, assignmentId) =>
    this.rtdb.ref(
      `users/${uid}/resourceManagement/assignments/${assignmentId}`,
    );

  doSaveUserAssignment = async (
    uid,
    assignmentId,
    assignmentData,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.userAssignment(uid, assignmentId).set(
      assignmentData,
    );
    await this.maybeLogOrgActivity(organization, {
      action: 'RESOURCE_ASSIGNMENT_SAVED',
      actorUid: actorUid || uid,
      actorEmail,
      details: { assignmentId },
    });
    return result;
  };

  doDeleteUserAssignment = async (
    uid,
    assignmentId,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.userAssignment(
      uid,
      assignmentId,
    ).remove();
    await this.maybeLogOrgActivity(organization, {
      action: 'RESOURCE_ASSIGNMENT_DELETED',
      actorUid: actorUid || uid,
      actorEmail,
      details: { assignmentId },
    });
    return result;
  };

  doSaveUserResourceManagementState = async (
    uid,
    state,
    organization = null,
    actorUid = null,
    actorEmail = null,
  ) => {
    const result = await this.userResourceManagement(uid).set(state);
    await this.maybeLogOrgActivity(organization, {
      action: 'RESOURCE_STATE_SAVED',
      actorUid: actorUid || uid,
      actorEmail,
      details: {},
    });
    return result;
  };

  doGetUserResourceManagementState = async (uid) => {
    const snapshot =
      await this.userResourceManagement(uid).once('value');
    return snapshot.val();
  };

  // *** Blog/Articles API (Firestore) ***

  articlesCollection = () => this.fsdb.collection('articles');

  articleDoc = (slug) => this.articlesCollection().doc(slug);

  doSaveArticle = async (articleData) => {
    const slug =
      articleData.slug || this.generateSlug(articleData.title);
    return this.articleDoc(slug).set(
      {
        ...articleData,
        slug,
        updatedAt: app.firestore.FieldValue.serverTimestamp(),
        publishedAt:
          articleData.publishedAt ||
          app.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true },
    );
  };

  doGetPublishedArticles = async (limit = 50) => {
    const snapshot = await this.articlesCollection()
      .where('status', '==', 'published')
      .get();
    const articles = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    articles.sort((a, b) => {
      const aTime = a.publishedAt?.toDate
        ? a.publishedAt.toDate().getTime()
        : 0;
      const bTime = b.publishedAt?.toDate
        ? b.publishedAt.toDate().getTime()
        : 0;
      return bTime - aTime;
    });
    return articles.slice(0, limit);
  };

  doGetArticlesByTag = async (tag) => {
    const snapshot = await this.articlesCollection()
      .where('status', '==', 'published')
      .where('tags', 'array-contains', tag)
      .get();
    const articles = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    articles.sort((a, b) => {
      const aTime = a.publishedAt?.toDate
        ? a.publishedAt.toDate().getTime()
        : 0;
      const bTime = b.publishedAt?.toDate
        ? b.publishedAt.toDate().getTime()
        : 0;
      return bTime - aTime;
    });
    return articles;
  };

  doGetArticle = async (slug) => {
    const doc = await this.articleDoc(slug).get();
    return doc.exists ? { id: doc.id, ...doc.data() } : null;
  };

  doGetAllArticles = async () => {
    const snapshot = await this.articlesCollection().get();
    const articles = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    articles.sort((a, b) => {
      const aTime = a.updatedAt?.toDate
        ? a.updatedAt.toDate().getTime()
        : 0;
      const bTime = b.updatedAt?.toDate
        ? b.updatedAt.toDate().getTime()
        : 0;
      return bTime - aTime;
    });
    return articles;
  };

  doDeleteArticle = (slug) => this.articleDoc(slug).delete();

  generateSlug = (title) =>
    title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

  generateUniqueSlug = async (title, excludeSlug = null) => {
    let slug = this.generateSlug(title);
    const baseSlug = slug;
    let counter = 1;
    while (true) {
      if (slug === excludeSlug) return slug;
      const doc = await this.articleDoc(slug).get();
      if (!doc.exists) return slug;
      slug = `${baseSlug}-${counter}`;
      counter++;
    }
  };

  doIncrementArticleViews = (slug) =>
    this.articleDoc(slug).update({
      views: app.firestore.FieldValue.increment(1),
    });

  // *** Feature Completion Dashboard API (Firestore) ***

  featureDashboardCollection = (uid) =>
    this.fsdb
      .collection('users')
      .doc(uid)
      .collection('featureDashboard');

  featureDashboardDoc = (uid) =>
    this.featureDashboardCollection(uid).doc('features');

  doSaveFeatureCompletionData = async (uid, features) => {
    try {
      return await this.featureDashboardDoc(uid).set(
        {
          features,
          updatedAt: app.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error('Error saving feature completion data:', error);
      throw error;
    }
  };

  doGetFeatureCompletionData = async (uid) => {
    try {
      const doc = await this.featureDashboardDoc(uid).get();
      return doc.exists ? doc.data().features || [] : null;
    } catch (error) {
      console.error('Error fetching feature completion data:', error);
      throw error;
    }
  };

  onFeatureCompletionDataChange = (uid, callback) => {
    try {
      return this.featureDashboardDoc(uid).onSnapshot(
        (doc) => {
          if (doc.exists) callback(doc.data().features || []);
        },
        (error) => {
          console.error(
            'Error listening to feature completion data:',
            error,
          );
        },
      );
    } catch (error) {
      console.error('Error setting up listener:', error);
    }
  };

  // *** Email Signup/Waitlist API (Firestore) ***

  emailSignupsCollection = () =>
    this.fsdb.collection('email_signups');

  doSaveEmailSignup = async (email) => {
    try {
      const docRef = await this.emailSignupsCollection().add({
        email: email.toLowerCase().trim(),
        subscribedAt: app.firestore.FieldValue.serverTimestamp(),
        source: 'landing_page',
      });
      return docRef.id;
    } catch (error) {
      console.error('Error saving email signup:', error);
      throw error;
    }
  };

  doEmailExists = async (email) => {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const snapshot = await this.emailSignupsCollection()
        .where('email', '==', normalizedEmail)
        .get();
      return snapshot.size > 0;
    } catch (error) {
      console.error('Error checking email:', error);
      throw error;
    }
  };

  doGetEmailSignups = async () => {
    try {
      const snapshot = await this.emailSignupsCollection()
        .orderBy('subscribedAt', 'desc')
        .get();
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching email signups:', error);
      throw error;
    }
  };
}

export default Firebase;
